"use client";

import { useId } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InlineCombobox } from "@/components/ui/inline-combobox";
import { StateAutocomplete } from "./state-autocomplete";
import {
  StandaloneAddressAutocomplete as AddressAutocomplete,
  type ParsedAddress,
} from "./address-autocomplete";
import { ZipInput } from "./zip-input";
import type { ZipLocation } from "@/hooks/use-zip-geocoding";
import type {
  AddressValue,
  AddressFieldErrors,
  AddressFieldKey,
} from "./address-input-types";
import { ADDRESS_CONSTRAINTS } from "@shared/formats/address";

interface AddressInputFullFormProps {
  value: AddressValue;
  onChange: (address: AddressValue) => void;
  streetDisplay: string;
  onStreetAutocomplete: (
    displayValue: string,
    parsed: ParsedAddress | null,
  ) => void;
  onZipGeocode: (result: ZipLocation | null) => void;
  onCityChange: (city: string) => void;
  onStateChange: (state: string) => void;
  isCompact: boolean;
  isInline: boolean;
  showLine2: boolean;
  showFieldLabels: boolean;
  disabled: boolean;
  errors: AddressFieldErrors;
  required: boolean;
  optionalFields?: ReadonlyArray<AddressFieldKey>;
  hiddenFields?: ReadonlyArray<AddressFieldKey>;
  className?: string;
  layout?: "paired";
}

type FieldProps = AddressInputFullFormProps & { fieldId: string };

export function AddressInputFullForm(props: AddressInputFullFormProps) {
  const fieldId = useId();
  const fieldProps = { ...props, fieldId };
  return (
    <div
      className={cn(
        props.isInline ? "flex items-center gap-2" : "grid gap-3",
        props.className,
      )}
    >
      <StreetField {...fieldProps} />
      {props.showLine2 && <SuiteField {...fieldProps} />}
      <div
        className={cn(
          props.isInline
            ? "flex items-center gap-2"
            : props.layout === "paired" ? "grid grid-cols-1 gap-3 sm:grid-cols-2" : "grid grid-cols-[1fr_1fr_100px] gap-2",
        )}
      >
        <CityField {...fieldProps} />
        <RegionField {...fieldProps} />
        <PostalField {...fieldProps} />
        {props.layout === "paired" ? <CountryField {...fieldProps} /> : null}
      </div>
    </div>
  );
}

function CountryField(props: FieldProps) {
  const inputId = `${props.fieldId}-country`;
  return (
    <div className="space-y-1">
      <Label htmlFor={inputId}>Country</Label>
      <InlineCombobox
        id={inputId}
        ariaLabel="Country"
        placeholder="Select country"
        value={props.value.country}
        options={[{ value: "US", label: "United States" }, { value: "CA", label: "Canada" }]}
        disabled={props.disabled}
        onSelect={(country) => {
          if (country !== props.value.country) props.onChange({ ...props.value, country, state: "", postalCode: "" });
        }}
      />
    </div>
  );
}

function fieldLabel(props: FieldProps, field: AddressFieldKey) {
  if (!props.required) return "";
  return props.optionalFields?.includes(field) ? " (optional)" : " *";
}

function StreetField(props: FieldProps) {
  const inputId = `${props.fieldId}-line1`;
  const errorId = props.errors.line1 ? `${inputId}-error` : undefined;
  return (
    <div
      className={cn(
        "space-y-1",
        props.isInline && "flex-1",
        props.hiddenFields?.includes("line1") && "hidden",
      )}
    >
      <Label
        htmlFor={inputId}
        className={cn(
          "text-xs text-muted-foreground",
          (!props.showFieldLabels || props.isInline) && "sr-only",
        )}
      >
        Street address{fieldLabel(props, "line1")}
      </Label>
      <AddressAutocomplete
        id={inputId}
        value={props.streetDisplay}
        onChange={props.onStreetAutocomplete}
        placeholder={props.isInline ? "Street" : "Start typing the address"}
        disabled={props.disabled}
        compact={props.isCompact}
        aria-invalid={!!props.errors.line1}
        aria-describedby={errorId}
        className={cn(props.errors.line1 && "[&_input]:border-destructive")}
      />
      {props.errors.line1 && (
        <p id={errorId} className="text-xs text-destructive-strong">
          {props.errors.line1}
        </p>
      )}
    </div>
  );
}

function SuiteField(props: FieldProps) {
  const inputId = `${props.fieldId}-line2`;
  return (
    <div className="space-y-1">
      <Label
        htmlFor={inputId}
        className={cn(
          "text-xs text-muted-foreground",
          !props.showFieldLabels && "sr-only",
        )}
      >
        Suite / Unit
      </Label>
      <Input
        id={inputId}
        value={props.value.line2 ?? ""}
        onChange={(event) =>
          props.onChange({ ...props.value, line2: event.target.value })
        }
        placeholder="Suite 100"
        autoComplete="address-line2"
        maxLength={ADDRESS_CONSTRAINTS.line2.maxLength}
        disabled={props.disabled}
        size={props.isCompact ? "compact" : "default"}
      />
    </div>
  );
}

function CityField(props: FieldProps) {
  const inputId = `${props.fieldId}-city`;
  const errorId = props.errors.city ? `${inputId}-error` : undefined;
  return (
    <div className={cn("space-y-1", props.isInline && "w-36")}>
      <Label
        htmlFor={inputId}
        className={cn(
          "text-xs text-muted-foreground",
          (!props.showFieldLabels || props.isInline) && "sr-only",
        )}
      >
        City{fieldLabel(props, "city")}
      </Label>
      <Input
        id={inputId}
        value={props.value.city}
        onChange={(event) => props.onCityChange(event.target.value)}
        placeholder="City"
        autoComplete="address-level2"
        maxLength={ADDRESS_CONSTRAINTS.city.maxLength}
        disabled={props.disabled}
        className={cn(props.errors.city && "border-destructive")}
        aria-invalid={!!props.errors.city}
        aria-describedby={errorId}
        size={props.isCompact ? "compact" : "default"}
      />
      {props.errors.city && (
        <p id={errorId} className="text-xs text-destructive-strong">
          {props.errors.city}
        </p>
      )}
    </div>
  );
}

function RegionField(props: FieldProps) {
  const inputId = `${props.fieldId}-state`;
  const errorId = props.errors.state ? `${inputId}-error` : undefined;
  return (
    <div className={cn("space-y-1", props.isInline && "w-28")}>
      <Label
        htmlFor={inputId}
        className={cn(
          "text-xs text-muted-foreground",
          (!props.showFieldLabels || props.isInline) && "sr-only",
        )}
      >
        State{fieldLabel(props, "state")}
      </Label>
      <StateAutocomplete
        id={inputId}
        includeCanada
        value={props.value.state}
        onChange={props.onStateChange}
        placeholder="State"
        compact={props.isCompact}
        disabled={props.disabled}
        error={!!props.errors.state}
        aria-describedby={errorId}
      />
      {props.errors.state && (
        <p id={errorId} className="text-xs text-destructive-strong">
          {props.errors.state}
        </p>
      )}
    </div>
  );
}

function PostalField(props: FieldProps) {
  const inputId = `${props.fieldId}-postalCode`;
  const errorId = props.errors.postalCode ? `${inputId}-error` : undefined;
  return (
    <div
      className={cn(
        "space-y-1",
        props.isInline && "w-24",
        props.hiddenFields?.includes("postalCode") && "hidden",
      )}
    >
      <Label
        htmlFor={inputId}
        className={cn(
          "text-xs text-muted-foreground",
          (!props.showFieldLabels || props.isInline) && "sr-only",
        )}
      >
        {props.value.country === "CA" ? "Postal code" : "ZIP"}
        {fieldLabel(props, "postalCode")}
      </Label>
      <PostalInput {...props} />
      {props.errors.postalCode && (
        <p id={errorId} className="text-xs text-destructive-strong">
          {props.errors.postalCode}
        </p>
      )}
    </div>
  );
}

function PostalInput(props: FieldProps) {
  const inputId = `${props.fieldId}-postalCode`;
  const errorId = props.errors.postalCode ? `${inputId}-error` : undefined;
  return (
    <>
      {props.value.country === "CA" ? (
        <Input
          id={inputId}
          value={props.value.postalCode}
          onChange={(event) =>
            props.onChange({
              ...props.value,
              postalCode: event.target.value.toUpperCase(),
            })
          }
          placeholder="Postal code"
          autoComplete="postal-code"
          disabled={props.disabled}
          aria-invalid={!!props.errors.postalCode}
          aria-describedby={errorId}
          size={props.isCompact ? "compact" : "default"}
        />
      ) : (
        <ZipInput
          id={inputId}
          value={props.value.postalCode}
          onChange={(postalCode) =>
            props.onChange({ ...props.value, postalCode })
          }
          onGeocode={props.onZipGeocode}
          placeholder="ZIP"
          compact={props.isCompact}
          disabled={props.disabled}
          aria-invalid={!!props.errors.postalCode}
          aria-describedby={errorId}
        />
      )}
    </>
  );
}
